import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X } from 'lucide-react';
import type { DiscrepancyFilters as Filters } from '@/types/discrepancy-review.types';

interface Props {
  filters: Filters;
  onFilterChange: (partial: Partial<Filters>) => void;
  onSearch: () => void;
  onClear: () => void;
  locations: Array<{ id: string; name: string }>;
  categories: Array<{ id: number; name: string }>;
  loading: boolean;
}

export const DiscrepancyFilters: React.FC<Props> = ({
  filters, onFilterChange, onSearch, onClear, locations, categories, loading
}) => {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Local */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Local</label>
            <Select
              value={filters.locationId || 'none'}
              onValueChange={value => onFilterChange({ locationId: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar local" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={String(loc.id)}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Turno */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Turno</label>
            <Select
              value={filters.shift}
              onValueChange={value => onFilterChange({ shift: value as Filters['shift'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="mañana">Mañana</SelectItem>
                <SelectItem value="tarde">Tarde</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tipo */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Tipo</label>
            <Select
              value={filters.type}
              onValueChange={value => onFilterChange({ type: value as Filters['type'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="transfer">Transferencia</SelectItem>
                <SelectItem value="purchase_order">Orden de Compra</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Categoría */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Categoría</label>
            <Select
              value={filters.categoryId || 'all'}
              onValueChange={value => onFilterChange({ categoryId: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fecha Inicio */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Fecha Inicio</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={e => onFilterChange({ dateFrom: e.target.value })}
            />
          </div>

          {/* Fecha Fin */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Fecha Fin</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={e => onFilterChange({ dateTo: e.target.value })}
            />
          </div>

          {/* Solo Diferencia */}
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={filters.onlyDiscrepancies}
                onChange={e => onFilterChange({ onlyDiscrepancies: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">Solo Diferencia</span>
            </label>
          </div>

          {/* Botones */}
          <div className="flex items-end gap-2">
            <Button
              onClick={onSearch}
              disabled={loading || !filters.locationId}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button variant="outline" onClick={onClear}>
              <X className="h-4 w-4 mr-1" />
              Limpiar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
